<template>
  <v-card>
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      @submit.prevent="
        loading = true;
        edit ? valid && editRoom() : valid && addRoom();
      "
    >
      <v-app-bar flat dark color="primary">
        <v-toolbar-title v-if="edit">Edit Room</v-toolbar-title>
        <v-toolbar-title v-else>Add Room</v-toolbar-title>
      </v-app-bar>
      <v-card-text class="pt-3">
        <v-container grid-list-lg class="pt-0">
          <v-text-field
            v-model="room.name"
            label="Name*"
            :rules="[v => !!v || 'Name is required']"
            required
          />
          <v-textarea v-model="room.description" label="Description" />
          <v-layout row wrap class="mb-2">
            <v-flex xs12 sm6>
              <v-text-field
                v-model="room.price"
                label="Price (per hour)*"
                :rules="[v => !!v || 'Price must be a number']"
                required
                type="number"
              />
            </v-flex>
            <v-flex xs12 sm6>
              <v-text-field
                v-model="room.capacity"
                label="Capacity*"
                :rules="[v => !!v || 'Capacity must be a number']"
                required
                type="number"
              />
            </v-flex>
          </v-layout>
          <v-radio-group
            v-model="room.room_type"
            row
            :rules="[v => !!v || 'Select a room type']"
            class="mb-3"
          >
            <template v-slot:label>
              <span>Room Type:</span>
            </template>
            <v-radio
              label="Meeting Room"
              value="Meeting Room"
              color="primary"
            />
            <v-radio label="Boardroom" value="Boardroom" color="primary" />
          </v-radio-group>
          <v-flex xs12 md6>
            <upload-image
              path="rooms"
              :min-dimensions="[1125, 812]"
              :max-file-size="5000000"
              :edit-url="edit ? room.image_download_url : null"
              @downloadpath="setDownloadUrl($event)"
            />
          </v-flex>
          <small>*indicates required field</small>
        </v-container>
      </v-card-text>
      <v-card-actions class="pt-0">
        <v-spacer />
        <v-btn color="error" text @click="cancel()">Cancel</v-btn>
        <v-btn
          v-if="edit"
          color="success"
          :disabled="!valid"
          type="submit"
          :loading="loading"
          >Update</v-btn
        >
        <v-btn
          v-else
          color="success"
          :disabled="!valid"
          type="submit"
          :loading="loading"
          >Add</v-btn
        >
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { roomsCollection } from '@/firebaseConfig';
import UploadImage from '@/components/UploadImage';

const blankRoom = {
  name: '',
  description: '',
  price: '',
  capacity: null,
  image: null,
  image_download_url: null,
  room_type: 'Meeting Room'
};

export default {
  name: 'AddRoom',
  components: {
    UploadImage
  },
  props: {
    edit: {
      default: null,
      type: Object
    }
  },
  data() {
    return {
      room: {
        name: '',
        description: '',
        price: '',
        capacity: null,
        image: null,
        image_download_url: null,
        room_type: 'Meeting Room'
      },
      valid: true,
      loading: false
    };
  },
  computed: {
    currentLocationId() {
      return this.$store.state.currentLocation.id;
    }
  },
  mounted() {
    this.initRoom();
  },
  methods: {
    initRoom() {
      if (this.edit) {
        this.room = { ...this.edit };
      } else {
        this.room = blankRoom;
        this.$refs.form.reset();
      }
    },
    setDownloadUrl(event) {
      if (event) {
        this.room.image = event.path;
        this.room.image_download_url = event.url;
      } else {
        this.room.image = '';
        this.room.image_download_url = null;
      }
    },
    cancel() {
      this.$refs.form.reset();
      this.$emit('close');
    },

    addRoom() {
      if (this.$refs.form.validate()) {
        let room = { ...this.room };
        Object.keys(room).forEach(key =>
          room[key] === undefined ? delete room[key] : ''
        );
        roomsCollection(this.currentLocationId)
          .add(room)
          .then(() => {
            this.$emit('close');
            this.$store.dispatch('createSnackbar', {
              color: 'success',
              text: `New room created successfully: ${this.room.name}`
            });
            this.room = blankRoom;
            this.loading = false;
          })
          .catch(error => {
            this.$store.dispatch('createSnackbar', {
              color: 'error',
              text: `Error adding room: ${error}`
            });
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    },
    editRoom() {
      if (this.$refs.form.validate()) {
        let room = { ...this.room };
        if (!this.room.image) {
          this.room.image = this.edit.image;
        }
        Object.keys(room).forEach(key =>
          room[key] === undefined ? delete room[key] : ''
        );
        roomsCollection(this.currentLocationId)
          .doc(this.edit.id)
          .set(this.room)
          .then(() => {
            this.$emit('close');
            this.$store.dispatch('createSnackbar', {
              color: 'success',
              text: `Room updated successfully: ${this.room.name}`
            });
            this.room = blankRoom;
            this.loading = false;
          })
          .catch(() => {
            this.$store.dispatch('createSnackbar', {
              color: 'error',
              text: `Error updating room: ${this.room.name}`
            });
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    }
  }
};
</script>
